import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getUserPermissions } from "../utils/permissions";
import toast from "react-hot-toast";
import AccessPINOverlay from "../components/AccessPINModal";
import { requestErpOtp, verifyErpOtp, getErpToken } from "../erpClient";
import AuthLayout from "../components/AuthLayout";
import { trackEvent, getTenantName } from "../services/analytics";
import "../components/auth-fields.css";
import "../components/AuthLayout.css";
import { isValidMobileNumber, sanitizeMobileInput } from "../utils/mobileInput";

const REMEMBER_PHONE_KEY = "authRememberPhone";
const OTP_LENGTH = 6;

function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tenantId] = useState(1);

  const [otp, setOtp] = useState("");
  const [showAccessPinModal, setShowAccessPinModal] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_PHONE_KEY);
    if (saved && /^[6-9]\d{9}$/.test(saved)) {
      setPhone(saved);
      setRemember(true);
    }

    const pendingMobile = sessionStorage.getItem("loginMobile");
    const pendingTenant = sessionStorage.getItem("loginTenantId");
    if (
      pendingMobile &&
      pendingTenant &&
      /^[6-9]\d{9}$/.test(pendingMobile)
    ) {
      setPhone(pendingMobile);
      setStep("otp");
    }
  }, []);

  useEffect(() => {
    if (step === "otp") {
      const timer = window.setTimeout(() => inputRefs.current[0]?.focus(), 120);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  const formatPhoneForDisplay = (digits) => {
    if (!digits) return "";
    return digits.split("").join(" ");
  };

  const handlePhoneChange = (e) => {
    if (step === "otp") return;
    const value = sanitizeMobileInput(e.target.value);
    setPhone(value);
    if (errors.phone) {
      setErrors({ ...errors, phone: "" });
    }
  };

  const validatePhone = () => {
    const newErrors = {};
    if (!phone.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (!isValidMobileNumber(phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDigit = (index) => otp[index] ?? "";

  const focusInput = (index) => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleDigitChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, "").slice(-1);
    const chars = otp.split("");
    while (chars.length < OTP_LENGTH) {
      chars.push("");
    }
    chars[index] = value;
    const nextOtp = chars.join("").slice(0, OTP_LENGTH);
    setOtp(nextOtp);

    if (value && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !getDigit(index) && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    setOtp(pasted);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const clearOtp = () => {
    setOtp("");
    focusInput(0);
  };

  const sendOtp = async () => {
    const result = await requestErpOtp({ tenantId, phone });
    if (result?.success && result.data?.sent) {
      if (remember) {
        localStorage.setItem(REMEMBER_PHONE_KEY, phone);
      } else {
        localStorage.removeItem(REMEMBER_PHONE_KEY);
      }
      sessionStorage.setItem("loginMobile", phone);
      sessionStorage.setItem("loginTenantId", String(tenantId));
      if (result.data.otp) {
        sessionStorage.setItem("sentOtp", result.data.otp);
      }
      toast.success(result.message || "OTP sent successfully!");
      setStep("otp");
      clearOtp();
      return true;
    }
    toast.error(result?.message || "Failed to send OTP. Please try again.");
    return false;
  };

  const handleChangeNumber = () => {
    setStep("phone");
    clearOtp();
    sessionStorage.removeItem("loginMobile");
    sessionStorage.removeItem("loginTenantId");
    sessionStorage.removeItem("sentOtp");
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      const result = await requestErpOtp({ tenantId, phone });
      if (result?.success && result.data?.sent) {
        if (result.data.otp) {
          sessionStorage.setItem("sentOtp", result.data.otp);
        }
        toast.success(result.message || "OTP sent successfully");
        clearOtp();
      } else {
        toast.error(
          result?.message || "Failed to resend OTP. Please try again.",
        );
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error("Please enter complete OTP");
      return;
    }

    const sentOtp = sessionStorage.getItem("sentOtp");
    if (sentOtp && otp !== sentOtp) {
      toast.error("Invalid OTP. Please try again.");
      clearOtp();
      return;
    }

    const result = await verifyErpOtp({
      tenantId,
      phone,
      otp,
    });

    if (!result?.success) {
      toast.error(result?.message || "Verification failed. Please try again.");
      return;
    }

    sessionStorage.removeItem("loginMobile");
    sessionStorage.removeItem("sentOtp");
    sessionStorage.removeItem("loginTenantId");

    toast.success(result.message || "OTP verified successfully");
    setShowAccessPinModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === "phone") {
      if (!validatePhone()) return;
      setIsLoading(true);
      try {
        await sendOtp();
      } catch (error) {
        console.error("OTP request error:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to send OTP. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      await verifyOtp();
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error("Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccessPinSuccess = (result) => {
    setShowAccessPinModal(false);

    trackEvent("Authentication", "Login", getTenantName());
    const erpToken = getErpToken();
    const targetPath =
      result?.pinType === "dihran"
        ? "/girvi"
        : result?.pinType === "quote"
          ? "/quote/dashboard"
          : "/dashboard";

    if (erpToken) {
      navigate(targetPath);
      return;
    }

    const permissions = getUserPermissions();
    if (!permissions) {
      handleChangeNumber();
      return;
    }

    navigate(targetPath);
  };

  const handleAccessPinCancel = () => {
    setShowAccessPinModal(false);
    toast("AccessPIN cancelled. Please try again.");
  };

  const maskedPhone =
    phone.length === 10
      ? `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`
      : `+91 ${formatPhoneForDisplay(phone)}`;

  const submitDisabled =
    step === "phone"
      ? isLoading || phone.length !== 10
      : isLoading || otp.length !== OTP_LENGTH;

  const submitLabel =
    step === "phone"
      ? isLoading
        ? "Sending code…"
        : "Continue"
      : isLoading
        ? "Verifying…"
        : "Verify & continue";

  return (
    <>
      <AuthLayout>
        <div className="auth-form__head auth-form__head--login">
          <h1 className="auth-form__head-title">
            {step === "phone" ? (
              <>Login</>
            ) : (
              <>
                Enter your <strong>OTP</strong>
              </>
            )}
          </h1>
          <p className="auth-form__head-sub">
            {step === "phone" ? (
              <>Enter your mobile number to login</>
            ) : (
              <>We sent a 6-digit code to {maskedPhone}</>
            )}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-form__field">
            <div className="auth-form__field-label-row">
              <label htmlFor="phone" className="form-label">
                Mobile number
              </label>
              {step === "otp" && (
                <button
                  type="button"
                  className="auth-form__link auth-form__change-phone"
                  onClick={handleChangeNumber}
                >
                  Change number
                </button>
              )}
            </div>
            <div
              className={`auth-field__control auth-field__control--phone ${errors.phone ? "is-invalid" : ""} ${step === "otp" ? "auth-field__control--locked" : ""}`}
            >
              <span className="auth-field__prefix">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                className="auth-field__input"
                id="phone"
                value={formatPhoneForDisplay(phone)}
                onChange={handlePhoneChange}
                placeholder="1234567890"
                maxLength={19}
                autoComplete="tel"
                readOnly={step === "otp"}
                aria-invalid={errors.phone ? "true" : undefined}
              />
            </div>
            {errors.phone && (
              <div className="invalid-feedback d-block">{errors.phone}</div>
            )}
          </div>

          {step === "otp" && (
            <div className="auth-form__otp-block">
              <label className="form-label" htmlFor="otp-digit-0">
                Security code
              </label>
              <div className="auth-otp-grid" onPaste={handlePaste}>
                {Array.from({ length: OTP_LENGTH }, (_, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    id={index === 0 ? "otp-digit-0" : undefined}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={getDigit(index)}
                    onChange={(e) => handleDigitChange(index, e)}
                    onKeyDown={(e) => handleDigitKeyDown(index, e)}
                    className="form-control text-center"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              <p className="auth-form__resend">
                Didn&apos;t receive a code?{" "}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className="btn btn-link p-0 auth-form__link align-baseline"
                  disabled={isLoading}
                >
                  Resend
                </button>
              </p>
            </div>
          )}

          <button
            type="submit"
            className={`btn btn-primary auth-form__submit${isLoading ? " auth-form__submit--loading" : ""}`}
            disabled={submitDisabled}
            aria-busy={isLoading}
          >
            {submitLabel}
          </button>

          <div className="auth-form__footer-box">
            <p className="auth-form__footer">
              Don&apos;t have an account? <Link to="/SignUp">Sign up</Link>
            </p>
          </div>
        </form>
      </AuthLayout>

      {showAccessPinModal && (
        <AccessPINOverlay
          show={showAccessPinModal}
          listenToIdle={false}
          onSuccess={handleAccessPinSuccess}
          onCancel={handleAccessPinCancel}
        />
      )}
    </>
  );
}

export default Login;
